/**
 * Orientation Editor view-model + (de)serialization.
 *
 * Decodes the flat `visible_*` rows the portal subscribes to into the nested
 * shape the editor renders (orientation -> ordered stops -> per-version beat
 * timelines -> actions), and encodes editor actions back into the
 * `set_script_actions` entry shape.
 *
 * The action storage contract mirrors the module's `script_action_row`
 * (modules/minetwin/src/tables.rs): Command flattens scalar columns,
 * Narrate -> narrate_text/audio_ref, Wait -> duration_ms. The one "advanced"
 * command column the UI does not edit (look_at_blend_start) is carried
 * verbatim on the action so a round-trip save never drops it.
 */

import type {
  CheckEntry,
  CheckGrading,
  CheckType,
  CommandLocation,
  CommandTarget,
  CommandType,
  OrientationStatus,
  OrientationVersion,
  ScriptActionEntry,
  ScriptActionInput,
  ScriptSource,
} from '@/generated/minetwin/types';
import { fmtLength, type UnitSystem } from '@twin-builder/units';

export type VersionTag = OrientationVersion['tag'];
export type StatusTag = OrientationStatus['tag'];
export type CommandTypeTag = CommandType['tag'];
export type CheckTypeTag = CheckType['tag'];
export type GradingTag = CheckGrading['tag'];

/**
 * Single-length mode: clients author and assign `Default` only. The timed
 * variants (Min5/Min10/Min20/Min60) are dormant, reserved for the planned AI
 * generator (AGENT-AUTHORED-ORIENTATIONS-PLAN). Do not write timed rows until
 * that lands.
 *
 * The editor pins every read AND write to this slot. Decoding stays tolerant —
 * {@link groupScripts} still keys rows by whatever tag they carry, so a legacy
 * timed script survives a round-trip untouched (we simply never save it back).
 * There is deliberately no fallback that reads a timed slot when `Default` is
 * empty: that would make saves ambiguous. Until the Phase 4 data migration
 * runs, such an orientation shows an empty timeline.
 */
export const EDIT_VERSION: VersionTag = 'Default';

export const STATUS_TAGS: StatusTag[] = ['Active', 'Draft', 'Archived'];

/** Lifecycle meaning per status, for the header badge and status controls. */
export const STATUS_DESC: Record<StatusTag, string> = {
  Draft: 'Draft: only visible in this editor, not assignable to users yet.',
  Active: 'Active: live, visible in pickers and assignable (including auto-assign).',
  Archived: 'Archived: soft-removed, hidden from pickers; existing assignments are kept.',
};

export const COMMAND_TYPES: CommandTypeTag[] = [
  'HighlightTwin', 'HighlightGeo', 'FlyToTwin', 'FlyToLocation', 'FlyAlongPath',
  'LookAtTwin', 'LookAtLocation', 'FlyAndLookAt', 'OrbitTwin', 'FollowTwin',
  'StopCamera', 'SelectTwins', 'ClearSelection', 'SetTransparency',
  'SetCameraMode', 'ShowRelationships', 'HideRelationships', 'ShowTwinInfo',
];

export const CMD_LABEL: Partial<Record<CommandTypeTag, string>> = {
  FlyToTwin: 'Fly to', FlyToLocation: 'Fly to geo', LookAtTwin: 'Look at',
  LookAtLocation: 'Look at geo', FlyAndLookAt: 'Fly + look', OrbitTwin: 'Orbit',
  FollowTwin: 'Follow', StopCamera: 'Stop camera', HighlightTwin: 'Highlight',
  HighlightGeo: 'Highlight geo', SelectTwins: 'Select',
  ClearSelection: 'Clear selection', SetTransparency: 'Transparency',
  FlyAlongPath: 'Fly along path', SetCameraMode: 'Camera mode',
  ShowRelationships: 'Show relationships', HideRelationships: 'Hide relationships',
  ShowTwinInfo: 'Twin info',
};

/**
 * One-line description of what each command does at runtime, shown as the
 * Command dropdown's option hover and as the inline hint under the field.
 * Statements here are verified against the mp `cmd_*` reducers and the
 * runtime dispatcher (agents/minezero-agent), not guessed; keep them true.
 */
export const CMD_DESC: Record<CommandTypeTag, string> = {
  FlyToTwin: 'Fly the camera to a twin and stop in front of it.',
  FlyToLocation: 'Fly the camera to a map coordinate.',
  LookAtTwin: 'Turn the camera to face a twin without moving.',
  LookAtLocation: 'Turn the camera to face a map coordinate without moving.',
  FlyAndLookAt: 'Fly to one target while facing another for the whole flight.',
  OrbitTwin: 'Circle the camera around a twin. Continuous keeps orbiting until a Stop camera.',
  FollowTwin: 'Attach the camera to a moving twin at a fixed distance and pitch. Always continuous — follows until a Stop camera; how long it holds comes from the beat (narration / hold).',
  StopCamera: 'Stop a running Orbit or Follow and give the viewer back free camera control.',
  HighlightTwin: 'Glow-highlight the twin in the 3D view for the given duration.',
  HighlightGeo: "Highlight one of a twin's geo properties (a road, area or point overlay).",
  SelectTwins: "Select the twin on every participant's client, as if they clicked it.",
  ClearSelection: 'Clear the twin selection on every client.',
  SetTransparency: 'Fade the twin to an opacity (1 restores it). With a duration it auto-reverts; without one it stays faded until restored.',
  // First-class path command: the editor authors it with an ordered waypoint
  // list (see isPathCommand / PathField); the agent sends cmd_fly_along_path.
  FlyAlongPath: 'Fly the camera smoothly along an ordered path of map coordinates.',
  // SetCameraMode and Show/HideRelationships (all Effects lane) are authored in
  // the editor like any other command; an imported or agent-authored
  // row carrying one decodes and displays the same way. SetCameraMode's mode
  // rides `message`.
  SetCameraMode: "Switch the trainee's camera navigation mode (freeFly, orbit or walk).",
  ShowRelationships: 'Show the relationship lines of the twins (none listed = every twin).',
  HideRelationships: 'Hide the relationship lines of the twins (none listed = every twin).',
  ShowTwinInfo: 'Show an info panel for a twin with a chosen set of its property and telemetry values (none chosen = all of them).',
};

/** Which optional scalar fields each command type exposes in the inspector. */
export const CMD_FIELDS: Record<CommandTypeTag, string[]> = {
  // Opacity removed from Highlight; distance/pitch removed from the fly-to
  // commands (Orbit keeps them since they define the orbit geometry). Follow
  // takes distance + pitch like Orbit but is ALWAYS continuous — no rotateSpeed
  // (a follow doesn't revolve) and no duration/Timed toggle. It runs until a Stop
  // camera; how long its beat holds comes from the beat (narration / hold), like
  // any carried continuous motion (see isContinuousCam / beatDriver).
  // HighlightGeo takes twins + geo property names (mirroring cmd_highlight_geo:
  // twin_ids + property_names — it has no coordinates), NOT a location.
  // FlyAndLookAt takes two CommandTargets (fly + look), not a plain twin.
  HighlightTwin: ['twin', 'duration'], HighlightGeo: ['twin', 'properties', 'duration'],
  FlyToTwin: ['twin', 'duration'], FlyToLocation: ['geo', 'duration'],
  LookAtTwin: ['twin', 'duration'], LookAtLocation: ['geo', 'duration'],
  FlyAndLookAt: ['flyLook', 'duration'],
  OrbitTwin: ['twin', 'distance', 'pitch', 'rotateSpeed', 'duration'],
  FollowTwin: ['twin', 'distance', 'pitch'], StopCamera: [], SelectTwins: ['twin'],
  ClearSelection: [], SetTransparency: ['twin', 'opacity', 'duration', 'dither'],
  FlyAlongPath: ['geo', 'duration'],
  SetCameraMode: ['mode'], ShowRelationships: ['twin', 'duration'], HideRelationships: ['twin'],
  ShowTwinInfo: ['twin', 'infoProperties', 'infoTelemetries', 'duration'],
};

/**
 * Twin/geo variants of the same verb, shown as ONE dropdown entry with a
 * Target toggle (Twin | Location, or Twin | Geo property for Highlight)
 * instead of two list entries. Purely a presentation grouping: the stored
 * commandType is still the concrete variant the runtime dispatches on.
 */
const PAIRS: [CommandTypeTag, CommandTypeTag][] = [
  ['FlyToTwin', 'FlyToLocation'],
  ['LookAtTwin', 'LookAtLocation'],
  ['HighlightTwin', 'HighlightGeo'],
];
export const CMD_PAIR: Partial<Record<CommandTypeTag, { twin: CommandTypeTag; geo: CommandTypeTag }>> = {};
for (const [twin, geo] of PAIRS) CMD_PAIR[twin] = CMD_PAIR[geo] = { twin, geo };

/** The twin arm of a paired command (identity for unpaired ones) — the value
 *  the merged Command dropdown shows and offers. */
export const canonicalCmd = (c: CommandTypeTag): CommandTypeTag => CMD_PAIR[c]?.twin ?? c;

/**
 * Twin dropdown options for a command, with its target-capability filter:
 * HighlightGeo only twins that have at least one geo property; FollowTwin
 * only twins whose location mode is Dynamic (a static twin can't be
 * followed). Everything else takes any project twin.
 */
export function twinOptionsForCommand(
  cmd: CommandTypeTag,
  all: string[],
  geoPropsByTwin?: Map<string, string[]>,
  dynamicTwinIds?: Set<string>,
): string[] {
  if (cmd === 'HighlightGeo') return all.filter((t) => (geoPropsByTwin?.get(t)?.length ?? 0) > 0);
  if (cmd === 'FollowTwin') return all.filter((t) => dynamicTwinIds?.has(t));
  return all;
}

export const LANES = [
  { key: 'camera', label: 'Camera', desc: 'Camera motion. One move per beat; Orbit and Follow keep running across later beats until a Stop camera.', cmds: ['FlyToTwin', 'FlyToLocation', 'FlyAlongPath', 'LookAtTwin', 'LookAtLocation', 'FlyAndLookAt', 'OrbitTwin', 'FollowTwin', 'StopCamera'] },
  { key: 'focus', label: 'Effects', desc: 'Visual effects: highlight, selection, transparency, relationship lines, camera mode.', cmds: ['HighlightTwin', 'HighlightGeo', 'SelectTwins', 'ClearSelection', 'SetTransparency', 'ShowRelationships', 'HideRelationships', 'SetCameraMode', 'ShowTwinInfo'] },
  { key: 'narrate', label: 'Narration', desc: 'Spoken narration (text-to-speech) with an on-screen caption. One per beat; its spoken length drives the beat.', cmds: [] as string[] },
  // Checks are not actions: they live on the step, keyed to the beat they fire
  // at the END of, so `laneOf` never returns this key.
  { key: 'checks', label: 'Checks', desc: 'Comprehension questions. A check fires when its beat has finished presenting, and the beat holds until the trainee resolves it.', cmds: [] as string[] },
  { key: 'wait', label: 'Hold', desc: 'Extra hold time added to the beat. One per beat.', cmds: [] as string[] },
] as const;

export type LaneKey = (typeof LANES)[number]['key'];

export const CONTINUOUS_CMDS: CommandTypeTag[] = ['OrbitTwin', 'FollowTwin'];

/** Lanes the runtime plays ONE action of per beat. The agent fires every
 *  command in a beat but speaks only the first narration and honours only the
 *  first hold, and the Unreal camera cancels a flight when the next camera
 *  command lands, so a second block in these lanes is dropped or overridden
 *  without a word. Effects stack, so that lane is not listed. */
export const SINGLE_PER_BEAT: readonly LaneKey[] = ['camera', 'narrate', 'wait'];

/** Whether `lane` already holds its one allowed action in `beat`. Always false
 *  for a lane that stacks. */
export function laneTaken(beat: Beat, lane: LaneKey): boolean {
  return SINGLE_PER_BEAT.includes(lane) && beat.actions.some((a) => laneOf(a) === lane);
}

/** First beat that carries two actions in a single-per-beat lane, as a save
 *  refusal, or null. Guards stops authored before the editor enforced it. */
export function beatsError(beats: Beat[]): string | null {
  for (let bi = 0; bi < beats.length; bi++) {
    for (const lane of SINGLE_PER_BEAT) {
      const n = beats[bi].actions.filter((a) => laneOf(a) === lane).length;
      if (n > 1) {
        const label = LANES.find((l) => l.key === lane)!.label.toLowerCase();
        return `Beat ${bi + 1} has ${n} ${label} actions: the trainee only gets one per beat. Move the extra to its own beat.`;
      }
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// View-model types
// ---------------------------------------------------------------------------
export type CommandAction = {
  kind: 'command';
  commandType: CommandTypeTag;
  twinIds: string[];
  latitude: number | null;
  longitude: number | null;
  altitude: number | null;
  durationMs: number | null;
  distance: number | null;
  pitch: number | null;
  rotateSpeed: number | null;
  opacity: number | null;
  /** SetTransparency only: fade with screen-door DITHER instead of smooth glass —
   *  for a model whose overlapping/duplicate geometry makes glass flicker. Persists
   *  in the command's (otherwise unused) `message` field, so no schema change. */
  dither: boolean;
  /** Fly along path only: fly the waypoints in REVERSE (end→start). Persists in
   *  the command's `message` field (like `dither`), so no schema change. */
  reverse: boolean;
  /** FlyAlongPath only: metres the camera rides ABOVE each waypoint's ground
   *  elevation while flying (eye height). Applied in the 3D preview and baked
   *  into the waypoint altitudes the agent sends; null ⇒ the CAM_EYE_HEIGHT_M
   *  default (~1.5 m). Rides the otherwise-unused `distance` column for
   *  FlyAlongPath, so no schema change. */
  eyeHeightOffset: number | null;
  message: string | null;
  propertyNames: string[];
  /** ShowTwinInfo only: the twin's telemetry names to display (properties ride
   *  `propertyNames`). Empty for every other command. */
  telemetryNames: string[];
  flyTarget: CommandTarget | null;
  lookAtTarget: CommandTarget | null;
  // Carried verbatim, not edited in the UI.
  lookAtBlendStart: number | null;
  /** FlyAlongPath only: ordered waypoints the camera flies in order (needs 2+).
   *  Empty/absent for every other command. The effective start (first point, or
   *  last when reversed) mirrors latitude/longitude/altitude so a client that
   *  ignores
   *  `path` still flies to the start. Stored in the typed `path` column — no JSON. */
  path: CommandLocation[];
};
export type NarrateAction = { kind: 'narrate'; text: string; audioRef: string | null };
export type WaitAction = { kind: 'wait'; ms: number };
export type EditorAction = CommandAction | NarrateAction | WaitAction;

export type Beat = { actions: EditorAction[] };

export type EditorStep = {
  stepId: string;
  projectId: string;
  name: string;
  lat: number;
  lon: number;
  el: number;
  durationMinutes: number;
  /** Per-version beat timelines. A version absent here has no authored script.
   *  The editor only ever renders/saves {@link EDIT_VERSION}; legacy timed
   *  slots ride along so a round-trip save leaves them untouched. */
  scripts: Partial<Record<VersionTag, Beat[]>>;
  /** Comprehension checks on this step, ordered by beat then authored order.
   *  Version-agnostic: a check hangs off the step, not off a script slot. */
  checks: EditorCheck[];
};

export type EditorOrientation = {
  orientationId: string;
  projectId: string;
  displayName: string;
  occupationTarget: string[];
  description: string;
  status: StatusTag;
  autoAssign: boolean;
  /** Check settings (set_orientation_check_settings). passingScorePct null =
   *  no pass/fail bar; maxAttempts only bites while autoReassignOnFail is on. */
  passingScorePct: number | null;
  showAnswersAtEnd: boolean;
  autoReassignOnFail: boolean;
  maxAttempts: number | null;
  /** `user_id` of the author who created it; empty on rows older than the column. */
  createdBy: string;
};

/**
 * What the header picker offers. Archived orientations are hidden unless the
 * user asks for them, and a non-empty `authorId` keeps only that author's
 * orientations. The current selection always stays listed: archiving the open
 * orientation, or filtering to another author, must not drop it out of its
 * own picker.
 */
export function pickerOrientations(
  list: EditorOrientation[],
  showArchived: boolean,
  currentId: string,
  authorId = '',
): EditorOrientation[] {
  return list.filter((o) =>
    o.orientationId === currentId
    || ((showArchived || o.status !== 'Archived') && (!authorId || o.createdBy === authorId)));
}

export type EditorModel = {
  orientations: EditorOrientation[];
  steps: Map<string, EditorStep>;
  /** Ordered stepIds per orientationId (by ascending position). */
  links: Map<string, string[]>;
  /** How many orientations link each stepId (a step reused in N orientations
   *  has usage N; an unlinked/orphan step has 0). Drives the reuse badge, the
   *  remove-vs-delete branch, and the save propagation warning. */
  usage: Map<string, number>;
};

// ---------------------------------------------------------------------------
// Decode: flat subscription rows -> nested view-model
// ---------------------------------------------------------------------------

// Loose row shapes (the generated Infer types are structurally compatible).
type AnyOpt<T> = T | undefined | null;
export type ScriptActionRow = {
  stepId: string;
  orientationVersion: OrientationVersion;
  beat: number;
  sequenceOrder: number;
  actionKind: { tag: 'Command' | 'Narrate' | 'Wait' };
  commandType: AnyOpt<CommandType>;
  twinIds: string[];
  propertyNames: string[];
  telemetryNames: AnyOpt<string[]>;
  latitude: AnyOpt<number>;
  longitude: AnyOpt<number>;
  altitude: AnyOpt<number>;
  durationMs: AnyOpt<number>;
  flyTarget: AnyOpt<CommandTarget>;
  lookAtTarget: AnyOpt<CommandTarget>;
  lookAtBlendStart: AnyOpt<number>;
  distance: AnyOpt<number>;
  pitch: AnyOpt<number>;
  rotateSpeed: AnyOpt<number>;
  opacity: AnyOpt<number>;
  message: AnyOpt<string>;
  path: AnyOpt<CommandLocation[]>;
  narrateText: AnyOpt<string>;
  audioRef: AnyOpt<string>;
};

const opt = <T>(v: AnyOpt<T>): T | null => (v == null ? null : v);

export function rowToAction(r: ScriptActionRow): EditorAction {
  switch (r.actionKind.tag) {
    case 'Narrate':
      return { kind: 'narrate', text: r.narrateText ?? '', audioRef: opt(r.audioRef) };
    case 'Wait':
      return { kind: 'wait', ms: r.durationMs ?? 0 };
    case 'Command':
    default:
      return {
        kind: 'command',
        commandType: (r.commandType?.tag as CommandTypeTag) ?? 'FlyToTwin',
        twinIds: r.twinIds ?? [],
        latitude: opt(r.latitude),
        longitude: opt(r.longitude),
        altitude: opt(r.altitude),
        durationMs: opt(r.durationMs),
        distance: opt(r.distance),
        pitch: opt(r.pitch),
        rotateSpeed: opt(r.rotateSpeed),
        opacity: opt(r.opacity),
        // FlyAlongPath rides the unused `distance` column for its eye-height offset.
        eyeHeightOffset: r.commandType?.tag === 'FlyAlongPath' ? opt(r.distance) : null,
        // SetTransparency carries its dither flag in `message`; FlyAlongPath
        // carries its "reverse" flag the same way. Decode into the typed field and
        // keep the client `message` clean for those commands.
        dither: r.commandType?.tag === 'SetTransparency' && r.message === 'dither',
        reverse: r.commandType?.tag === 'FlyAlongPath' && r.message === 'reverse',
        message: (r.commandType?.tag === 'SetTransparency' || r.commandType?.tag === 'FlyAlongPath') ? null : opt(r.message),
        propertyNames: r.propertyNames ?? [],
        telemetryNames: r.telemetryNames ?? [],
        flyTarget: opt(r.flyTarget),
        lookAtTarget: opt(r.lookAtTarget),
        lookAtBlendStart: opt(r.lookAtBlendStart),
        path: r.path ?? [],
      };
  }
}

/** Group a step+version's action rows into contiguous beat columns. */
export function groupBeats(rows: ScriptActionRow[]): Beat[] {
  if (!rows.length) return [];
  const byBeat = new Map<number, ScriptActionRow[]>();
  let max = -1;
  for (const r of rows) {
    max = Math.max(max, r.beat);
    const list = byBeat.get(r.beat) ?? [];
    list.push(r);
    byBeat.set(r.beat, list);
  }
  const beats: Beat[] = [];
  for (let i = 0; i <= max; i++) {
    const list = (byBeat.get(i) ?? []).slice().sort((a, b) => a.sequenceOrder - b.sequenceOrder);
    beats.push({ actions: list.map(rowToAction) });
  }
  return beats;
}

/**
 * Group one step's action rows into per-version beat timelines, keyed by
 * whatever tag each row carries. Deliberately tag-agnostic: legacy timed rows
 * decode alongside the `Default` ones so the editor's draft is a faithful copy
 * (it just never renders or saves them — see {@link EDIT_VERSION}).
 */
export function groupScripts(rows: ScriptActionRow[]): Partial<Record<VersionTag, Beat[]>> {
  const byVersion = new Map<VersionTag, ScriptActionRow[]>();
  for (const r of rows) {
    const v = r.orientationVersion.tag;
    const list = byVersion.get(v) ?? [];
    list.push(r);
    byVersion.set(v, list);
  }
  const scripts: Partial<Record<VersionTag, Beat[]>> = {};
  for (const [v, list] of byVersion) scripts[v] = groupBeats(list);
  return scripts;
}

// ---------------------------------------------------------------------------
// Encode: editor action -> set_script_actions entry
// ---------------------------------------------------------------------------
export function actionToInput(a: EditorAction): ScriptActionInput {
  if (a.kind === 'narrate') {
    return { tag: 'Narrate', value: { text: a.text, audioRef: a.audioRef ?? undefined } } as ScriptActionInput;
  }
  if (a.kind === 'wait') {
    return { tag: 'Wait', value: { ms: a.ms } } as ScriptActionInput;
  }
  // The single-point fallback (latitude/longitude/altitude) must mirror the
  // EFFECTIVE flight start — the last waypoint when `reverse` is set — so a
  // client that ignores `path` (today's cmdFlyToLocation) flies to the right
  // end. `path` itself stays in forward/authored order; the agent reverses it.
  const effStart =
    a.commandType === 'FlyAlongPath' && a.reverse && a.path && a.path.length > 0
      ? a.path[a.path.length - 1]
      : null;
  return {
    tag: 'Command',
    value: {
      commandType: { tag: a.commandType },
      twinIds: a.twinIds,
      // The inspector's comma-separated input keeps raw fragments while typing
      // ("a, " -> ['a', ' ']); normalize at encode so the row stays clean.
      propertyNames: a.propertyNames.map((s) => s.trim()).filter(Boolean),
      telemetryNames: a.telemetryNames.map((s) => s.trim()).filter(Boolean),
      latitude: effStart ? effStart.latitude : (a.latitude ?? undefined),
      longitude: effStart ? effStart.longitude : (a.longitude ?? undefined),
      altitude: effStart ? (effStart.altitude ?? undefined) : (a.altitude ?? undefined),
      durationMs: a.durationMs ?? undefined,
      flyTarget: a.flyTarget ?? undefined,
      lookAtTarget: a.lookAtTarget ?? undefined,
      lookAtBlendStart: a.lookAtBlendStart ?? undefined,
      // FlyAlongPath stows its eye-height offset in the unused `distance` column.
      distance: a.commandType === 'FlyAlongPath' ? (a.eyeHeightOffset ?? undefined) : (a.distance ?? undefined),
      pitch: a.pitch ?? undefined,
      rotateSpeed: a.rotateSpeed ?? undefined,
      opacity: a.opacity ?? undefined,
      // SetTransparency stows its dither flag in `message`; FlyAlongPath stows
      // its "reverse" flag the same way. Other commands keep their real message.
      message:
        a.commandType === 'SetTransparency'
          ? (a.dither ? 'dither' : undefined)
          : a.commandType === 'FlyAlongPath'
            ? (a.reverse ? 'reverse' : undefined)
            : (a.message ?? undefined),
      // FlyAlongPath's ordered waypoints. Only persisted when it actually holds
      // waypoints; latitude/longitude/altitude mirror the effective start above
      // for any client that ignores `path`.
      path: a.path && a.path.length > 0 ? a.path : [],
    },
  } as ScriptActionInput;
}

/** Flatten a beat timeline into ordered set_script_actions entries. */
export function beatsToEntries(beats: Beat[]): ScriptActionEntry[] {
  const out: ScriptActionEntry[] = [];
  beats.forEach((b, bi) => {
    for (const a of b.actions) out.push({ beat: bi, action: actionToInput(a) });
  });
  return out;
}

export const AUTHORED_SOURCE: ScriptSource = { tag: 'Authored' } as ScriptSource;

// ---------------------------------------------------------------------------
// Comprehension checks
// ---------------------------------------------------------------------------

/**
 * A check as the editor holds it: the `orientation_check` row merged with its
 * `orientation_check_key` row, so "correct" is a flag on the option instead of
 * a second id list to keep in sync while editing. Encoding splits them apart
 * again: the module stores the key in its own table so trainees never
 * subscribe to it.
 *
 * `checkType` is carried verbatim: only MultipleChoice can be authored today,
 * and a row of a future kind must survive a round trip rather than be rewritten
 * as MCQ.
 */
export type EditorCheckOption = { id: string; text: string; correct: boolean };
export type EditorCheck = {
  checkId: string;
  beat: number;
  checkType: CheckTypeTag;
  question: string;
  options: EditorCheckOption[];
  multiSelect: boolean;
  /** How the picks are graded, orthogonal to the input mode: All = the picked
   *  set equals the key, Any = one or more picks, every one in the key. Any is
   *  single-select only (see {@link checkError}). */
  grading: GradingTag;
  shuffleOptions: boolean;
  /** Extra tries after the first. null = unlimited (never a magic 0). */
  retryLimit: number | null;
  /** null = untimed. */
  timeLimitSeconds: number | null;
  showCorrectAnswer: boolean;
};

/** The kinds the module accepts today; the rest are schema-reserved.
 *  `WorldSelection` grades on a set of twin ids rather than a text option list
 *  (see {@link isWorldSelection}). */
export const CHECK_TYPES: CheckTypeTag[] = ['MultipleChoice', 'WorldSelection'];

/** A "select in the world" check: its answer is the set of twins the trainee
 *  must click, carried in `options` (one per expected twin, every one marked
 *  correct) so the existing key/grading plumbing needs no second shape. There
 *  is no authored text-option list, so the option EDITOR is a twin picker. */
export function isWorldSelection(c: EditorCheck): boolean {
  return c.checkType === 'WorldSelection';
}

export const CHECK_TYPE_LABEL: Record<CheckTypeTag, string> = {
  MultipleChoice: 'Multiple choice',
  OpenEnded: 'Open ended',
  WorldSelection: 'Select in world',
  WorldLocation: 'Go to location',
};

export type OrientationCheckRow = {
  checkId: string;
  stepId: string;
  projectId: string;
  beat: number;
  checkType: CheckType;
  question: string;
  options: { id: string; text: string }[];
  multiSelect: boolean;
  shuffleOptions: boolean;
  retryLimit: AnyOpt<number>;
  timeLimitSeconds: AnyOpt<number>;
  showCorrectAnswer: boolean;
  sequenceOrder: number;
  /** Absent on a row from a module that predates the field: decodes as All. */
  grading?: CheckGrading;
};

export type OrientationCheckKeyRow = { checkId: string; projectId: string; correctOptionIds: string[] };

/** Map key for one answer key. `check_id` is unique per PROJECT only (the
 *  module pairs it with `project_id` on every lookup for exactly this reason),
 *  and the editor's model spans every project the admin can see, so an
 *  unscoped map would hand one site's key to another site's check. */
export const checkKeyId = (projectId: string, checkId: string): string => `${projectId}/${checkId}`;

export function rowToCheck(r: OrientationCheckRow, correctOptionIds: string[]): EditorCheck {
  const correct = new Set(correctOptionIds);
  const c: EditorCheck = {
    checkId: r.checkId,
    beat: r.beat,
    checkType: r.checkType.tag,
    question: r.question,
    options: (r.options ?? []).map((o) => ({ id: o.id, text: o.text, correct: correct.has(o.id) })),
    multiSelect: r.multiSelect,
    grading: r.grading?.tag ?? 'All',
    shuffleOptions: r.shuffleOptions,
    retryLimit: opt(r.retryLimit),
    timeLimitSeconds: opt(r.timeLimitSeconds),
    showCorrectAnswer: r.showCorrectAnswer,
  };
  // World selection never authors the flag: re-derive it so an old row cannot
  // decode into a state the hidden switch could not fix.
  return isWorldSelection(c) ? worldSelectionOptions(c, c.options) : c;
}

/**
 * One step's check rows, merged with the answer keys (keyed by
 * {@link checkKeyId}) and ordered the way they fire (beat, then the module's
 * sparse `sequence_order`). A row with no key row decodes with nothing marked
 * correct, which the save validation then refuses loudly, rather than silently
 * inventing an answer.
 */
export function groupChecks(
  rows: OrientationCheckRow[],
  keys: Map<string, string[]>,
): EditorCheck[] {
  return rows
    .slice()
    .sort((a, b) => a.beat - b.beat || a.sequenceOrder - b.sequenceOrder)
    .map((r) => rowToCheck(r, keys.get(checkKeyId(r.projectId, r.checkId)) ?? []));
}

export function checkToEntry(c: EditorCheck): CheckEntry {
  return {
    checkId: c.checkId,
    beat: c.beat,
    checkType: { tag: c.checkType },
    question: c.question,
    options: c.options.map((o) => ({ id: o.id, text: o.text })),
    correctOptionIds: c.options.filter((o) => o.correct).map((o) => o.id),
    multiSelect: c.multiSelect,
    grading: { tag: c.grading },
    shuffleOptions: c.shuffleOptions,
    retryLimit: c.retryLimit ?? undefined,
    timeLimitSeconds: c.timeLimitSeconds ?? undefined,
    showCorrectAnswer: c.showCorrectAnswer,
  } as CheckEntry;
}

/** Flatten a step's checks into ordered set_step_checks entries (bulk replace;
 *  an empty list clears the step's checks). */
export function checksToEntries(checks: EditorCheck[]): CheckEntry[] {
  return checks
    .slice()
    .sort((a, b) => a.beat - b.beat)
    .map(checkToEntry);
}

/** A fresh MCQ on `beat`, seeded valid: three options with the first marked
 *  correct, so a newly added check never starts in the refused state. */
export function makeCheck(beat: number): EditorCheck {
  return {
    checkId: uid('chk'),
    beat,
    checkType: 'MultipleChoice',
    question: '',
    options: [
      { id: uid('opt'), text: '', correct: true },
      { id: uid('opt'), text: '', correct: false },
      { id: uid('opt'), text: '', correct: false },
    ],
    multiSelect: false,
    grading: 'All',
    shuffleOptions: true,
    // One try: the common case for a comprehension check. retry_limit counts
    // EXTRA tries, so 0 is a single try; null would be unlimited.
    retryLimit: 0,
    timeLimitSeconds: null,
    showCorrectAnswer: true,
  };
}

/**
 * Move a check to another kind, rewriting the answer shape so it is never
 * stranded in a state the save refuses. MCQ → WorldSelection drops the text
 * options (the twins are picked fresh); WorldSelection → MCQ reseeds a default
 * multiple-choice set. Same-kind switches are a no-op. `question`, timing and
 * the retry/reveal settings are carried across untouched.
 */
export function switchCheckType(c: EditorCheck, checkType: CheckTypeTag): EditorCheck {
  if (checkType === c.checkType) return c;
  if (checkType === 'WorldSelection') {
    // Twins are added one at a time in the picker; start empty (invalid until
    // the first twin is picked, which checkError reports plainly).
    return { ...c, checkType, options: [], multiSelect: false, grading: 'All', shuffleOptions: false };
  }
  // Back to (or to any) option-list kind: reuse makeCheck's valid seed.
  const seed = makeCheck(c.beat);
  return { ...c, checkType, options: seed.options, multiSelect: false, grading: 'All', shuffleOptions: true };
}

/** The expected twin ids of a WorldSelection check (its options ARE the twins,
 *  each marked correct). Order is authoring order. */
export function worldSelectionTwinIds(c: EditorCheck): string[] {
  return c.options.map((o) => o.id);
}

/** Add a twin to a WorldSelection check's expected set (deduped; the picker
 *  should already hide picked twins). Every expected twin is marked correct, so
 *  the shared key/grading path needs no special case. `multiSelect` is derived
 *  from the set size (see {@link worldSelectionOptions}): the flag only picks
 *  the input mode, and a set of two can only be answered in multi-select. */
export function addWorldSelectionTwin(c: EditorCheck, twinId: string, label: string): EditorCheck {
  if (c.options.some((o) => o.id === twinId)) return c;
  return worldSelectionOptions(c, [...c.options, { id: twinId, text: label, correct: true }]);
}

/** Replace a WorldSelection check's expected set, keeping `multiSelect` in
 *  step with it and the grading rule: one twin, or Any, is a single click; All
 *  over two or more is pick-all-then-submit. The author never sets the flag by
 *  hand for this kind. */
export function worldSelectionOptions(c: EditorCheck, options: EditorCheckOption[]): EditorCheck {
  return { ...c, options, multiSelect: c.grading === 'All' && options.length > 1 };
}

/** Whether the All / Any choice means anything: only with two or more correct
 *  answers (expected twins, or marked options). */
export function hasGradingChoice(c: EditorCheck): boolean {
  return correctOptionIds(c).length > 1;
}

/** Replace a check's options (either kind). Below two correct answers the rule
 *  falls back to All, so a hidden control never holds Any (and never keeps the
 *  Multi-select switch disabled with nothing on screen to explain why). */
export function withCheckOptions(c: EditorCheck, options: EditorCheckOption[]): EditorCheck {
  const grading = options.filter((o) => o.correct).length > 1 ? c.grading : 'All';
  return isWorldSelection(c) ? worldSelectionOptions({ ...c, grading }, options) : { ...c, options, grading };
}

/** Set the grading rule, keeping the input mode honest: Any is single-select,
 *  All over several correct answers is multi-select. */
export function setCheckGrading(c: EditorCheck, grading: GradingTag): EditorCheck {
  return { ...c, grading, multiSelect: grading === 'All' && correctOptionIds(c).length > 1 };
}

/**
 * Beat indices that carry at least one action, in ANY authored slot: the beats
 * a check may hang off. `set_step_checks` rejects the whole call when a check's
 * beat has no `script_action` row, so an empty beat cannot hold a check. Legacy
 * slots count for the same reason the module counts them: a step scripted only
 * at Min20 still has real beats.
 */
export function authoredBeats(s: EditorStep): Set<number> {
  const out = new Set<number>();
  for (const beats of Object.values(s.scripts)) {
    beats?.forEach((b, i) => { if (b.actions.length) out.add(i); });
  }
  return out;
}

/**
 * Why this check cannot be saved, or null. Mirrors what the module refuses in
 * `validate_check_entries`: the beat must exist in the step's script, the key
 * must be a non-empty subset of the options, and the grading rule table holds:
 * All + single-select takes exactly one correct answer; Any takes one or more
 * and is single-select only.
 */
export function checkError(c: EditorCheck, beats: Set<number>, twinIds?: Set<string>): string | null {
  if (!beats.has(c.beat)) return `Beat ${c.beat + 1} has no actions: a check needs a beat that presents something.`;
  if (isWorldSelection(c)) {
    // The answer is the set of twins to click. Every option is an expected twin
    // (all marked correct), so its count is the expected-set size.
    const ids = worldSelectionTwinIds(c);
    if (ids.length === 0) return 'Pick at least one twin the trainee must select.';
    // The module cannot see the twin catalog cross-module, so twin-EXISTENCE is
    // the authoring layer's job: refuse an id that is no longer a project twin.
    if (twinIds) {
      const gone = ids.find((id) => !twinIds.has(id));
      if (gone) return `Twin ${gone} is no longer in this project — remove it or pick another.`;
    }
  } else if (correctOptionIds(c).length === 0) return 'Mark the correct answer.';
  if (c.grading === 'Any' && c.multiSelect) return '"Any one of these" is single-select: turn Multi-select off.';
  if (c.grading === 'All' && !c.multiSelect && correctOptionIds(c).length > 1) {
    return 'A single-select check takes exactly one correct answer.';
  }
  return null;
}

/** First invalid check in a step, as "Check N: reason", or null. `twinIds`, when
 *  given, lets World-selection checks flag an expected twin that has left the
 *  project. */
export function checksError(checks: EditorCheck[], beats: Set<number>, twinIds?: Set<string>): string | null {
  for (let i = 0; i < checks.length; i++) {
    const e = checkError(checks[i], beats, twinIds);
    if (e) return `Check ${i + 1} (beat ${checks[i].beat + 1}): ${e}`;
  }
  return null;
}

/** Timeline caption for a check block. */
export function checkLabel(c: EditorCheck): string {
  const q = c.question.trim();
  return q ? `✓ ${q.slice(0, 20)}${q.length > 20 ? '…' : ''}` : '✓ new check';
}

export function checkHover(c: EditorCheck): string {
  const noun = isWorldSelection(c) ? 'twin' : 'option';
  const lines = [
    c.question.trim() || 'No question yet',
    `${CHECK_TYPE_LABEL[c.checkType]} · ${c.options.length} ${noun}${c.options.length === 1 ? '' : 's'}${c.multiSelect ? ' · multi-select' : ''}${c.grading === 'Any' ? ' · any one is correct' : ''}`,
    c.retryLimit == null ? 'Unlimited tries' : c.retryLimit === 0 ? 'One try' : `${c.retryLimit + 1} tries in all`,
  ];
  if (c.timeLimitSeconds != null) lines.push(`${c.timeLimitSeconds}s to answer`);
  lines.push('The beat holds until this is answered');
  return lines.join('\n');
}

// --- local (no-DB) grading, for the Play-orientation quiz preview -----------
// The authored key lives on the options themselves (`correct`), so the preview
// grades in the browser — no `submit_check_answer`, nothing written to STDB.

/** The option ids the author marked correct. */
export function correctOptionIds(c: EditorCheck): string[] {
  return c.options.filter((o) => o.correct).map((o) => o.id);
}

/** Grade a submission locally, mirroring the module's `selection_is_correct`.
 *  All: the picked set must exactly equal the correct set (single-select is
 *  just a one-element case). Any: at least one pick, and every pick correct.
 *  An empty pick (timer expiry) is always wrong unless an All check somehow
 *  has no correct option. */
export function gradeCheck(c: EditorCheck, selected: string[]): boolean {
  const correct = new Set(correctOptionIds(c));
  const picked = new Set(selected);
  if (c.grading === 'Any') return picked.size > 0 && [...picked].every((id) => correct.has(id));
  if (correct.size !== picked.size) return false;
  for (const id of picked) if (!correct.has(id)) return false;
  return true;
}

/** Total tries allowed, or `null` for unlimited. `retryLimit` counts EXTRA
 *  tries after the first (module semantics), so total = retryLimit + 1. */
export function totalTries(c: EditorCheck): number | null {
  return c.retryLimit == null ? null : c.retryLimit + 1;
}

/** The checks that fire at the END of `beat`, in play order (sequence within a
 *  beat is authored order — the array already holds it). */
export function checksForBeat(checks: EditorCheck[], beat: number): EditorCheck[] {
  return checks.filter((c) => c.beat === beat);
}

/** Checks a passing score works out to, at the orientation's current size.
 *  Multiply before dividing: `(pct / 100) * total` lands a float hair above an
 *  exact integer for some inputs (56% of 50 is 28.000000000000004), which would
 *  round the shown count one higher than the score the module actually passes. */
export function requiredCorrect(pct: number, total: number): number {
  return Math.ceil((pct * total) / 100);
}

/** "80% · 13/16 correct": the score is stored as a percent, but is always
 *  shown with the count it currently means (spec: recomputed as checks
 *  are added or removed). */
export function passingScoreLabel(pct: number | null, total: number): string {
  if (pct == null) return 'No pass mark: checks are informational';
  return `${pct}% · ${requiredCorrect(pct, total)}/${total} correct`;
}

// ---------------------------------------------------------------------------
// Lanes + beat timing (author-time estimates; runtime uses pre-warmed TTS)
// ---------------------------------------------------------------------------
export function laneOf(a: EditorAction): LaneKey {
  if (a.kind === 'narrate') return 'narrate';
  if (a.kind === 'wait') return 'wait';
  return (LANES[0].cmds as readonly string[]).includes(a.commandType) ? 'camera' : 'focus';
}

// Narration length is estimated PER CHARACTER, not per word: a long word takes
// longer to speak than a short one, so counting words under/over-estimates a
// beat's spoken length. ~15 chars/sec (~900/min) is a typical narration pace and
// ≈ the previous 2.7 words/sec at a normal word length, so existing tours keep
// roughly the same total while varying word lengths now time correctly. (Runtime
// still uses the pre-warmed TTS audio length; this drives the author-time
// estimate and the TTS-less "Play orientation" preview.)
const CHARS_PER_SEC = 15;
export function narrMs(text: string): number {
  if (!text) return 0;
  const chars = text.trim().length;
  return chars ? Math.max(1200, Math.round((chars / CHARS_PER_SEC) * 1000)) : 0;
}

export function isContinuousCam(a: EditorAction): boolean {
  // FollowTwin is ALWAYS continuous — it has no Timed mode; how long its beat
  // holds comes from the beat (narration / hold), never an authored camera
  // duration. OrbitTwin keeps the Continuous/Timed split (continuous only while
  // it has no duration).
  return (
    a.kind === 'command' &&
    CONTINUOUS_CMDS.includes(a.commandType) &&
    (a.commandType === 'FollowTwin' || a.durationMs == null)
  );
}

/**
 * Per-beat carry-over of a continuous camera command (Orbit/Follow) started on
 * an earlier beat. A continuous camera control keeps running across later beats
 * until a `StopCamera` (or another camera command takes over). Returns, for
 * each beat, the still-running continuous command to surface as a "continues
 * until stop" hint, or null. The beat that starts the command renders its own
 * block, so only later beats with no camera action of their own get the hint.
 */
export function continuousCamCarry(beats: Beat[]): (CommandAction | null)[] {
  const out: (CommandAction | null)[] = [];
  let active: CommandAction | null = null;
  for (const beat of beats) {
    const carriedIn = active;
    let ownCam = false;
    for (const a of beat.actions) {
      if (a.kind !== 'command' || laneOf(a) !== 'camera') continue;
      ownCam = true;
      // StopCamera ends it; a new continuous replaces it; any other camera
      // move (fly/look) takes over and ends the prior continuous control.
      if (a.commandType === 'StopCamera') active = null;
      else if (isContinuousCam(a)) active = a;
      else active = null;
    }
    out.push(carriedIn && !ownCam ? carriedIn : null);
  }
  return out;
}

export function gateMs(a: EditorAction): number {
  if (a.kind === 'narrate') return narrMs(a.text);
  if (a.kind === 'wait') return a.ms ?? 0;
  if (a.kind === 'command') return isContinuousCam(a) ? 0 : (a.durationMs ?? 0);
  return 0;
}

export function beatDriver(beat: Beat): { ms: number; driver: 'narration' | 'camera' | 'hold' } {
  let ms = 0;
  let driver: 'narration' | 'camera' | 'hold' = 'hold';
  for (const a of beat.actions) {
    const g = gateMs(a);
    if (g > ms) {
      ms = g;
      driver = a.kind === 'narrate' ? 'narration' : a.kind === 'command' ? 'camera' : 'hold';
    }
  }
  return { ms, driver };
}

/**
 * Effective Fly + look targets. Legacy editor rows (the old single-twin UI)
 * stored only twinIds with both targets null — read that twin as the fly
 * target so old rows surface their intent; the look target has no legacy
 * equivalent. The runtime dispatcher (minezero-agent command-dispatch.ts)
 * applies the same fallback.
 */
export function flyLookTargets(a: CommandAction): { fly: CommandTarget | null; look: CommandTarget | null } {
  const fly = a.flyTarget ?? (a.twinIds[0] ? ({ tag: 'Twin', value: a.twinIds[0] } as CommandTarget) : null);
  return { fly, look: a.lookAtTarget };
}

/**
 * The geo point a SELECTED command should reveal in 3D (the pulsing marker and
 * the fly-to focus), or null when it carries no location. Geo fly/look commands
 * use their own scalar lat/lon/alt columns (falling back to the stop's); Fly +
 * look stores its targets as CommandTargets, so read a Location target there —
 * the LOOK target (the point faced) preferred over the FLY target. Twin targets
 * return null (they surface via the twin highlight, not a geo marker).
 */
export function commandLocation(
  a: CommandAction,
  fallbackLat: number,
  fallbackLon: number,
  fallbackEl: number,
): { lat: number; lon: number; el: number } | null {
  if ((CMD_FIELDS[a.commandType] ?? []).includes('geo')) {
    return {
      lat: a.latitude ?? fallbackLat,
      lon: a.longitude ?? fallbackLon,
      el: a.altitude ?? fallbackEl,
    };
  }
  if (a.commandType === 'FlyAndLookAt') {
    const { fly, look } = flyLookTargets(a);
    const loc =
      (look?.tag === 'Location' ? look.value : null) ??
      (fly?.tag === 'Location' ? fly.value : null);
    if (loc) return { lat: loc.latitude, lon: loc.longitude, el: loc.altitude ?? fallbackEl };
  }
  return null;
}

/**
 * A selected command's location targets split by role, for the two 3D markers:
 * `look` (orange `highlightGeo`) and `fly` (green `highlightFly`). A single-geo
 * command (FlyToLocation / LookAtLocation) fills `look` only; Fly + look fills
 * each side from its own Location target (a Twin target leaves that side null —
 * it surfaces via the twin glow instead). {@link commandLocation} stays the
 * "one representative point" (look ?? fly) used for the camera focus and 2D map.
 */
export function commandGeoMarkers(
  a: CommandAction,
  fallbackLat: number,
  fallbackLon: number,
  fallbackEl: number,
): {
  look: { lat: number; lon: number; el: number } | null;
  fly: { lat: number; lon: number; el: number } | null;
} {
  if ((CMD_FIELDS[a.commandType] ?? []).includes('geo')) {
    return {
      look: { lat: a.latitude ?? fallbackLat, lon: a.longitude ?? fallbackLon, el: a.altitude ?? fallbackEl },
      fly: null,
    };
  }
  if (a.commandType === 'FlyAndLookAt') {
    const { fly, look } = flyLookTargets(a);
    const loc = (t: CommandTarget | null) =>
      t?.tag === 'Location' ? { lat: t.value.latitude, lon: t.value.longitude, el: t.value.altitude ?? fallbackEl } : null;
    return { look: loc(look), fly: loc(fly) };
  }
  return { look: null, fly: null };
}

/**
 * The twin a SELECTED command targets, to highlight in 3D on selection — the twin
 * counterpart to {@link commandLocation}. A twin-targeted command uses its first
 * twinId; Fly + look reads a Twin fly/look target (look preferred, matching
 * commandLocation). A geo-only command (or one with no twin) returns null — it
 * surfaces via the geo marker instead.
 */
export function commandTargetTwin(a: CommandAction): string | null {
  if ((CMD_FIELDS[a.commandType] ?? []).includes('twin')) {
    return a.twinIds[0] || null;
  }
  if (a.commandType === 'FlyAndLookAt') {
    const { fly, look } = flyLookTargets(a);
    const t = (look?.tag === 'Twin' ? look.value : null) ?? (fly?.tag === 'Twin' ? fly.value : null);
    return t || null;
  }
  return null;
}

/** Short caption for a CommandTarget: twin display name (id fallback) or the coords. */
export function targetLabel(t: CommandTarget | null, twinNames?: Map<string, string>): string {
  if (!t) return '?';
  if (t.tag === 'Twin') return t.value ? (twinNames?.get(t.value) ?? t.value.replace(/^MZ-/, '')) : '?';
  return `◎ ${t.value.latitude.toFixed(4)}, ${t.value.longitude.toFixed(4)}`;
}

export function blockLabel(a: EditorAction, twinNames?: Map<string, string>): string {
  if (a.kind === 'narrate') return a.text ? `“${a.text.slice(0, 18)}${a.text.length > 18 ? '…' : ''}”` : 'narration…';
  if (a.kind === 'wait') return `${((a.ms ?? 0) / 1000).toFixed(1)}s`;
  if (a.commandType === 'FlyAndLookAt') {
    const { fly, look } = flyLookTargets(a);
    return `${CMD_LABEL.FlyAndLookAt} ${targetLabel(fly, twinNames)} 👁 ${targetLabel(look, twinNames)}`;
  }
  // FlyAlongPath shows its waypoint count.
  if (isPathCommand(a)) return `Fly along path · ${commandPoints(a).length}`;
  const t = CMD_LABEL[a.commandType] ?? a.commandType;
  // Twin caption: resolved display name (TWIN-DISPLAY-NAME) when known, else
  // the id with the legacy MZ- prefix stripped.
  const first = a.twinIds[0];
  const firstLabel = first ? (twinNames?.get(first) ?? first.replace(/^MZ-/, '')) : '';
  const tw = first ? ` ${firstLabel}${a.twinIds.length > 1 ? ` +${a.twinIds.length - 1}` : ''}` : '';
  return `${t}${tw}`;
}

/**
 * Multi-line hover text for a timeline block: what the action does plus its
 * authored parameters. Native title tooltips render the \n line breaks.
 */
export function blockHover(a: EditorAction, twinNames?: Map<string, string>, units: UnitSystem = 'metric'): string {
  if (a.kind === 'narrate') return a.text ? `Narration (spoken via text-to-speech):\n“${a.text}”` : 'Narration, no text yet';
  if (a.kind === 'wait') return `Hold this beat for ${((a.ms ?? 0) / 1000).toFixed(1)}s`;
  if (isPathCommand(a)) {
    const n = commandPoints(a).length;
    const parts = [`Fly the camera along a path of ${n} waypoint${n === 1 ? '' : 's'}.`];
    if (a.durationMs != null) parts.push(`Duration: ${(a.durationMs / 1000).toFixed(1)}s`);
    return parts.join('\n');
  }
  const name = (id: string) => {
    const dn = twinNames?.get(id);
    return dn ? `${dn} (${id})` : id;
  };
  const lines: string[] = [CMD_DESC[a.commandType] ?? a.commandType];
  if (a.commandType === 'FlyAndLookAt') {
    const { fly, look } = flyLookTargets(a);
    lines.push(`Fly to: ${targetLabel(fly, twinNames)}`, `Look at: ${targetLabel(look, twinNames)}`);
  } else if (a.twinIds.length) {
    lines.push(`Target: ${a.twinIds.map(name).join(', ')}`);
  } else if (a.latitude != null || a.longitude != null) {
    lines.push(`Location: ${(a.latitude ?? 0).toFixed(5)}, ${(a.longitude ?? 0).toFixed(5)}`);
  }
  if (a.commandType === 'HighlightGeo' && a.propertyNames.length) {
    lines.push(`Geo property: ${a.propertyNames.join(', ')}`);
  }
  if (isContinuousCam(a)) lines.push('Continuous: runs until a Stop camera');
  else if (a.durationMs != null) lines.push(`Duration: ${(a.durationMs / 1000).toFixed(1)}s`);
  if (a.distance != null) lines.push(`Distance: ${fmtLength(a.distance / 100, units)}`); // stored in cm
  if (a.pitch != null) lines.push(`Pitch: ${a.pitch}°`);
  if (a.rotateSpeed != null) lines.push(`Rotate: ${(a.rotateSpeed / 6).toFixed(1)} laps/min`);
  if (a.opacity != null) lines.push(`Opacity: ${a.opacity}`);
  return lines.join('\n');
}

export function uid(prefix: string): string {
  // Math.random is fine here: client-side id for a brand-new step before save.
  return `${prefix}_${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Whether a camera command has a 3D-viewer preview counterpart (a cinematic
 * bridge verb) AND enough authored target to drive it. Twin fly/look need a
 * target twin; geo fly/look always work (they fall back to the stop's coords);
 * FlyAndLookAt needs BOTH targets to be twins (the viewer verb is twin→twin
 * only); Orbit/Follow need a target twin and preview as a continuous motion
 * (toggle Stop). StopCamera has no standalone preview. Keeps the Inspector's
 * "Preview in 3D" gate and the editor's `previewCommand` switch in one rule.
 */
export function previewableCommand(a: CommandAction): boolean {
  switch (a.commandType) {
    case 'FlyToTwin':
    case 'LookAtTwin':
    // Orbit / Follow are continuous — the viewer runs them until stopCamera, so
    // their preview button toggles to "Stop Preview" (see CONTINUOUS_CMDS).
    case 'OrbitTwin':
    case 'FollowTwin':
    // Highlight verbs resolve their twin to a geo point and preview via the
    // viewer's pulsing `highlightGeo` marker (see previewCommand) — exactly how
    // playback renders a Highlight effect. Both need a target twin to resolve.
    case 'HighlightTwin':
    case 'HighlightGeo':
    // SelectTwins previews via the viewer's `setSelection` (the twin's selection
    // outline) — the same visual playback drives; needs a target twin.
    case 'SelectTwins':
    // SetTransparency previews via the viewer's `setTwinTransparency` material
    // fade at the command's opacity — needs a target twin to fade.
    case 'SetTransparency':
    // ShowTwinInfo previews via the viewer's `showTwinInfo` verb — the viewer
    // resolves the chosen property/telemetry values + the twin's screen position
    // and the pane draws the floating card; needs a target twin.
    case 'ShowTwinInfo':
      return Boolean(a.twinIds[0]);
    case 'FlyToLocation':
    case 'LookAtLocation':
    // FlyAlongPath previews via the viewer's `flyAlongPath` spline verb — needs
    // its waypoints, validated separately (isPathCommand / ≥2 points).
    case 'FlyAlongPath':
    // ClearSelection needs no target — it clears the whole selection set.
    case 'ClearSelection':
    // SetCameraMode previews via the viewer's `setCameraMode` verb — switches the
    // camera's navigation mode (free fly / orbit / walk); needs no target.
    case 'SetCameraMode':
    // Show/HideRelationships preview via the viewer's `setRelationships` (per
    // twin) / `setRelationshipLinesVisible` (no twins = every twin) verbs; no
    // target required.
    case 'ShowRelationships':
    case 'HideRelationships':
      return true;
    case 'FlyAndLookAt': {
      const { fly, look } = flyLookTargets(a);
      // Both targets present; each may be a twin OR a geo location — the viewer's
      // `flyToLookAt` verb resolves either. Twin resolvability is checked
      // separately in cameraTargetIssue.
      return Boolean(fly) && Boolean(look);
    }
    default:
      return false;
  }
}

/** True when this action is the first-class "Fly along path" command. Its
 *  waypoints live in `path`; {@link commandPoints} falls back to the single
 *  latitude/longitude for a degenerate (0/1-point) path. */
export function isPathCommand(a: CommandAction): boolean {
  return a.commandType === 'FlyAlongPath';
}

/**
 * The ordered waypoints a path command edits. Lives in `path`; if empty, fall
 * back to the single `latitude`/`longitude`/`altitude` as a one-item list. No
 * location at all = empty list.
 */
export function commandPoints(a: CommandAction): CommandLocation[] {
  if (a.path && a.path.length > 0) return a.path;
  if (a.latitude != null && a.longitude != null) {
    return [{ latitude: a.latitude, longitude: a.longitude, altitude: a.altitude ?? undefined }];
  }
  return [];
}

/**
 * The waypoints in FLIGHT order — reversed (end→start) when the command's
 * `reverse` flag is set. Used for playback and the start/end markers so both
 * follow the chosen direction.
 */
export function flightPoints(a: CommandAction): CommandLocation[] {
  const pts = commandPoints(a);
  return a.reverse ? [...pts].reverse() : pts;
}

/**
 * Write an edited waypoint list back onto a path command. Any non-empty list is
 * persisted in the typed `path` (so the editor keeps recognizing it as "Fly along
 * path", even at one waypoint); an empty list clears it back to a plain fly-to.
 * The first point always mirrors `latitude`/`longitude`/`altitude`, so a client
 * that ignores `path` still flies to the start.
 */
export function withCommandPoints(a: CommandAction, pts: CommandLocation[]): CommandAction {
  const first = pts[0];
  return {
    ...a,
    path: pts.length ? pts : [],
    latitude: first ? first.latitude : null,
    longitude: first ? first.longitude : null,
    altitude: first ? (first.altitude ?? null) : null,
  };
}

/** A fresh command action with every optional column unset. */
export function makeCommand(
  commandType: CommandTypeTag,
  twinIds: string[],
  durationMs: number | null = null,
): CommandAction {
  return {
    kind: 'command', commandType, twinIds,
    latitude: null, longitude: null, altitude: null, durationMs,
    distance: null, pitch: null, rotateSpeed: null, opacity: null, dither: false, reverse: false, eyeHeightOffset: null, message: null,
    propertyNames: [], telemetryNames: [], flyTarget: null, lookAtTarget: null, lookAtBlendStart: null, path: [],
  };
}

/** Author-time total duration estimate for a step's timeline (ms). */
export function estimateMs(beats: Beat[]): number {
  return beats.reduce((n, b) => n + beatDriver(b).ms, 0);
}

/** Expected dwell derived from the step's script: whole minutes, at least one. */
export function estimateDwellMinutes(step: Pick<EditorStep, 'scripts'>): number {
  return Math.max(1, Math.ceil(estimateMs(step.scripts[EDIT_VERSION] ?? []) / 60_000));
}

// ---------------------------------------------------------------------------
// Playback guards (PLAY-ORIENTATION-IN-WEB, issue #77)
// ---------------------------------------------------------------------------

/** Watchable floor for beats with no own timing. */
export const PLAY_MIN_HOLD_MS = 1200;
/** How long playback shows an open-ended Orbit/Follow before moving on. */
export const PLAY_CONTINUOUS_HOLD_MS = 3500;
/**
 * Hard ceiling on how long playback may hold any single beat. The Unreal
 * runtime wedged its control lock forever on a beat whose camera move never
 * completed (#77); the web play loop must never reproduce that — however
 * broken the authored timing (a duration typed in the wrong unit, a corrupt
 * row decoding to a huge number), the show moves on. Two minutes comfortably
 * covers the longest legitimate narration beat.
 */
export const PLAY_MAX_BEAT_HOLD_MS = 120_000;

/**
 * Bounded per-beat hold for the play loop: the longest of the beat's own
 * driver (narration / wait / authored camera duration), the camera flight it
 * kicked off, and the watchable floor — clamped to
 * {@link PLAY_MAX_BEAT_HOLD_MS} so no beat can hold the stage forever, with
 * non-finite or negative inputs treated as "no own timing" rather than
 * poisoning the timer. A beat that only rides a still-running Orbit/Follow
 * (no own timing at all) shows for {@link PLAY_CONTINUOUS_HOLD_MS}.
 */
export function playHoldMs(driverMs: number, camMs: number, continuousRunning: boolean): number {
  const sane = (n: number): number => (Number.isFinite(n) && n > 0 ? n : 0);
  const driver = sane(driverMs);
  const cam = sane(camMs);
  if (continuousRunning && driver === 0 && cam === 0) return PLAY_CONTINUOUS_HOLD_MS;
  return Math.min(Math.max(driver, cam, PLAY_MIN_HOLD_MS), PLAY_MAX_BEAT_HOLD_MS);
}

/** The twin knowledge {@link cameraTargetIssue} validates against. */
export type TwinResolvability = {
  /** Every twin id in the selected project's core (`digital_twin`). */
  knownTwinIds: ReadonlySet<string>;
  /** Twins the viewer can resolve to a position: a geo location
   *  (turbotwin_metadata) or a model asset bound to their dtmi. A site ROOT /
   *  grouping twin has neither. */
  flyableTwinIds: ReadonlySet<string>;
};

/**
 * Why the embedded viewer cannot drive this camera command, or null when it
 * can (#77 pre-validation). A camera verb aimed at a twin with no geometry or
 * location — the site root / a grouping twin — is a silent no-op in the web
 * viewer and wedges the Unreal runtime's control lock forever, so playback
 * and preview skip the move with this message instead of pretending to fly.
 *
 * Fails OPEN for twin ids core does not know at all (a stale reference, or a
 * subscription that has not delivered yet): only a twin that is present in
 * `knownTwinIds` but missing from `flyableTwinIds` is a confirmed dead end.
 * Geo-targeted commands always pass — they fall back to the stop's own
 * coordinates. Purely a viewer-side guard: it never rewrites authored data.
 */
export function cameraTargetIssue(
  a: CommandAction,
  twins: TwinResolvability,
  twinNames?: Map<string, string>,
): string | null {
  if (a.kind !== 'command' || laneOf(a) !== 'camera') return null;
  const label = (id: string) => {
    const dn = twinNames?.get(id);
    return dn ? `${dn} (${id})` : id;
  };
  const twinIssue = (id: string | undefined, what: string): string | null => {
    if (!id) return `${what} has no target twin authored`;
    if (twins.knownTwinIds.has(id) && !twins.flyableTwinIds.has(id)) {
      return `'${label(id)}' has no geometry or location to fly to — pick a concrete twin or a map location`;
    }
    return null;
  };
  switch (a.commandType) {
    case 'FlyToTwin':
    case 'LookAtTwin':
    case 'OrbitTwin':
    case 'FollowTwin':
      return twinIssue(a.twinIds[0], CMD_LABEL[a.commandType] ?? a.commandType);
    case 'FlyAndLookAt': {
      const { fly, look } = flyLookTargets(a);
      if (!fly || !look) return 'Fly + look needs both a fly and a look target';
      // Each target is a twin OR a geo location; the viewer's `flyToLookAt` verb
      // resolves either (a geo target falls back to its coordinates). Only twin
      // targets need to resolve to geometry/location.
      const flyIssue = fly.tag === 'Twin' ? twinIssue(fly.value, 'Fly target') : null;
      const lookIssue = look.tag === 'Twin' ? twinIssue(look.value, 'Look target') : null;
      return flyIssue ?? lookIssue;
    }
    default:
      // Geo fly/look reuse the stop's coords; StopCamera is always drivable.
      return null;
  }
}

export function cloneBeats(beats: Beat[]): Beat[] {
  return beats.map((b) => ({ actions: b.actions.map((a) => ({ ...a })) }));
}

export function cloneChecks(checks: EditorCheck[]): EditorCheck[] {
  return checks.map((c) => ({ ...c, options: c.options.map((o) => ({ ...o })) }));
}

/**
 * What saveStop writes for the edited beat list (#228 follow-up: deleting
 * every beat never reached the server).
 *  - 'write': beats to save.
 *  - 'clear': the author deleted every beat. The slot exists only once the
 *    stop's action rows were read (buildModel creates it from them), so an
 *    empty array is a deletion; clear only once the action view is loaded.
 *  - 'skip': no slot (no beats ever, or not loaded yet): nothing to write.
 */
export function scriptWrite(beats: readonly unknown[] | undefined, actionsLoaded: boolean): 'write' | 'clear' | 'skip' {
  if (beats && beats.length > 0) return 'write';
  if (beats && actionsLoaded) return 'clear';
  return 'skip';
}
